import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { User, FileText, Sparkles, ListChecks, Settings as SettingsIcon, LogOut, MessageSquareText, Rocket, Bot, CreditCard, Gift, Radar, Handshake, ShieldCheck, Linkedin, HeartPulse, Menu, LifeBuoy } from "lucide-react";
import { Logo } from "@/components/Logo";

const links = [
  { to: "/matches", label: "Job Matches", icon: Radar, id: "nav-matches" },
  { to: "/apply", label: "Quick Apply", icon: Rocket, id: "nav-apply" },
  { to: "/auto-apply", label: "Auto-Apply", icon: Bot, id: "nav-autoapply" },
  { to: "/profile", label: "Profile", icon: User, id: "nav-profile" },
  { to: "/cvs", label: "CVs", icon: FileText, id: "nav-cvs" },
  { to: "/tailor", label: "Tailor CV", icon: Sparkles, id: "nav-tailor" },
  { to: "/screening", label: "Screening", icon: MessageSquareText, id: "nav-screening" },
  { to: "/linkedin", label: "LinkedIn", icon: Linkedin, id: "nav-linkedin" },
  { to: "/nhs-supporting", label: "NHS Statement", icon: HeartPulse, id: "nav-nhs" },
  { to: "/tracker", label: "Tracker", icon: ListChecks, id: "nav-tracker" },
  { to: "/referrals", label: "Refer & Earn", icon: Gift, id: "nav-referrals" },
  { to: "/partners/dashboard", label: "Affiliate", icon: Handshake, id: "nav-affiliate" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "nav-billing" },
  { to: "/support", label: "Feedback & Support", icon: LifeBuoy, id: "nav-support" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, id: "nav-settings" },
];

const itemClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-[#1a3060] text-white" : "text-muted-foreground hover:text-foreground hover:bg-zinc-100"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = !!user?.is_admin;
  const isAffiliate = !!user?.is_affiliate;
  const navLinks = isAdmin
    ? [
        { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin" },
        { to: "/settings", label: "Settings", icon: SettingsIcon, id: "nav-settings" },
      ]
    : links.filter((l) => l.id !== "nav-affiliate" || isAffiliate);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = ({ onClick }) => (
    <>
      {navLinks.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink key={l.to} to={l.to} data-testid={l.id} onClick={onClick} className={itemClass}>
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            {l.label}
          </NavLink>
        );
      })}
    </>
  );

  return (
    <>
      {/* Persistent left sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-zinc-200 bg-white z-40" data-testid="sidebar">
        <div className="px-5 py-4 border-b border-zinc-200">
          <Logo imgClassName="h-6" testId="sidebar-logo" />
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="sidebar-links">
          <NavItems />
        </nav>
        <div className="px-4 pb-2 flex flex-col gap-1">
          <a href="/terms" className="text-xs text-muted-foreground hover:text-foreground">Terms &amp; Conditions</a>
          <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground">Privacy Policy</a>
        </div>
        <div className="p-3 border-t border-zinc-200">
          <Button variant="outline" className="w-full justify-start" onClick={handleLogout} data-testid="sidebar-logout-button">
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Logout
          </Button>
        </div>
      </aside>

      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-zinc-200 lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setOpen(true)}
                data-testid="nav-menu-button"
                aria-label="Open menu"
                className="lg:hidden flex items-center justify-center h-10 w-10 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 active:scale-95 transition-transform shrink-0"
              >
                <Menu className="h-5 w-5 text-[#1a3060]" strokeWidth={2} />
              </button>
              <div className="lg:hidden">
                <Logo imgClassName="h-6" testId="navbar-logo" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-xs text-muted-foreground max-w-[160px] truncate" data-testid="navbar-user-email">
                {user?.email}
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button" className="active:scale-[0.98] transition-transform">
                <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[280px] p-0 flex flex-col" data-testid="nav-drawer">
          <SheetHeader className="px-5 py-4 border-b border-zinc-200 text-left">
            <SheetTitle><Logo imgClassName="h-6" /></SheetTitle>
            <SheetDescription className="text-xs truncate">{user?.email}</SheetDescription>
          </SheetHeader>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="nav-drawer-links">
            <NavItems onClick={() => setOpen(false)} />
          </nav>
          <div className="px-4 flex flex-col gap-1 pt-1">
            <a href="/terms" className="text-xs text-muted-foreground hover:text-foreground">Terms &amp; Conditions</a>
            <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground">Privacy Policy</a>
          </div>
          <div className="p-3 border-t border-zinc-200">
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout} data-testid="drawer-logout-button">
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Logout
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
